import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Input, Select } from '@components/ui';
import { useBranchOffices } from '@features/companies/hooks/useBranchOffices';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { ReferenceSection } from './ReferenceSection';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';

interface Step2InvoiceDetailsProps {
  wizardData: InvoiceWizardData;
  onUpdate: (updates: Partial<InvoiceWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

// Catálogos de datos
const DOCUMENT_TYPES = [
  { id: '01', description: 'Factura de Operación Interna', hint: 'Ventas de bienes o servicios dentro de Panamá' },
  { id: '02', description: 'Factura de Importación', hint: 'Compra de bienes o servicios desde el exterior' },
  { id: '03', description: 'Factura de Exportación', hint: 'Venta de bienes o servicios hacia el exterior' },
  { id: '04', description: 'Nota de Crédito Referente a una o Varias FE', hint: 'Corrige o anula parcialmente una factura emitida' },
  { id: '05', description: 'Nota de Débito Referente a una o Varias FE', hint: 'Aumenta el monto de una factura emitida' },
  { id: '06', description: 'Nota de Crédito Genérica', hint: 'Ajuste de crédito sin referencia a una FE específica' },
  { id: '07', description: 'Nota de Débito Genérica', hint: 'Ajuste de débito sin referencia a una FE específica' },
  { id: '08', description: 'Factura de Zona Franca', hint: 'Operaciones realizadas en zonas francas' },
  { id: '09', description: 'Reembolso', hint: 'Documento de reembolso de gastos' },
  { id: '10', description: 'Factura de Operación Extranjera', hint: 'Operaciones con empresas o personas en el exterior' },
];

const OPERATION_TYPES = [
  { id: '1', description: 'Salida o venta' },
  { id: '2', description: 'Entrada o compra' },
];

const CONTAINER_SHIPPING = [
  { id: '1', description: 'Normal' },
  { id: '2', description: 'El receptor exceptúa al emisor de la obligatoriedad de envío del contenedor' },
];

const OPERATION_DESTINATIONS = [
  { id: '1', description: 'Panamá' },
  { id: '2', description: 'Extranjero' },
];

const OPERATION_NATURES = [
  { id: '01', description: 'Venta', foreign: false, hint: 'Venta regular de bienes o servicios dentro de Panamá' },
  { id: '02', description: 'Exportación', foreign: true, hint: 'Envío de bienes o servicios al exterior' },
  { id: '03', description: 'Re-exportación', foreign: true, hint: 'Bienes importados que se exportan nuevamente' },
  { id: '04', description: 'Venta de fuente extranjera', foreign: true, hint: 'Venta de bienes provenientes del extranjero' },
  { id: '05', description: 'Servicio de fuente extranjera', foreign: true, hint: 'Servicio prestado desde o hacia el exterior' },
  { id: '10', description: 'Transferencia/Traspaso', foreign: false, hint: 'Movimiento de bienes entre sucursales o entidades relacionadas' },
  { id: '11', description: 'Devolución', foreign: false, hint: 'Devolución de bienes previamente vendidos' },
  { id: '12', description: 'Consignación', foreign: false, hint: 'Entrega de bienes en consignación para venta por terceros' },
  { id: '13', description: 'Remesa', foreign: false, hint: 'Envío de mercancía para distribución' },
  { id: '14', description: 'Entrega gratuita', foreign: false, hint: 'Entrega de bienes sin costo (donaciones, muestras, etc.)' },
  { id: '20', description: 'Compra', foreign: false, hint: 'Factura de compra para comercio informal (ej: taxistas)' },
  { id: '21', description: 'Importación', foreign: true, hint: 'Compra de bienes provenientes del exterior' },
];

const SALES_TRANSACTION_TYPES = [
  { id: '1', description: 'Venta de giro del negocio', hint: 'Productos o mercancías propios de su actividad comercial' },
  { id: '2', description: 'Venta de activo fijo', hint: 'Equipos, maquinaria u otros activos fijos de la empresa' },
  { id: '3', description: 'Venta de bienes raíces', hint: 'Terrenos, edificios, locales u otras propiedades inmuebles' },
  { id: '4', description: 'Prestación de servicio', hint: 'Servicios profesionales, técnicos o de cualquier índole' },
];

const DOCUMENT_FORMAT_GENERATIONS = [
  { id: '1', description: 'Sin generación de CAFE' },
  { id: '2', description: 'Cinta de papel' },
  { id: '3', description: 'Papel formato carta' },
];

// INCOTERMS válidos según DGI Tabla 32 — con descripciones de la ficha técnica
const VALID_INCOTERMS = [
  { id: 'EXW', description: 'En fábrica', hint: 'El comprador asume todos los costos desde las instalaciones del vendedor' },
  { id: 'FCA', description: 'Franco porteador', hint: 'El vendedor entrega en un punto acordado del país de origen al transportista' },
  { id: 'FAS', description: 'Franco al costado del buque', hint: 'El vendedor entrega la mercancía en el muelle del puerto de carga' },
  { id: 'FOB', description: 'Franco a bordo', hint: 'El vendedor entrega la mercancía sobre el buque; el comprador gestiona el transporte' },
  { id: 'CFR', description: 'Costo y flete', hint: 'El vendedor paga transporte hasta el puerto de destino; riesgo pasa al cargar el buque' },
  { id: 'CIF', description: 'Costo, seguro y flete', hint: 'Igual que CFR pero el vendedor también paga el seguro hasta el puerto destino' },
  { id: 'CPT', description: 'Transporte pagado hasta', hint: 'El vendedor paga transporte hasta el destino; riesgo pasa al entregar al transportista' },
  { id: 'CIP', description: 'Transporte y seguro pagado hasta', hint: 'Igual que CPT pero el vendedor también paga el seguro hasta el destino' },
  { id: 'DAP', description: 'Entregada en lugar', hint: 'El vendedor asume todos los costos excepto importación, hasta el lugar de destino' },
  { id: 'DAT', description: 'Entregada en terminal', hint: 'El vendedor asume todos los costos hasta descargar en la terminal de destino' },
  { id: 'DDP', description: 'Entregada derechos pagados', hint: 'El vendedor paga todo incluyendo aduana de importación; el comprador solo recibe' },
];

// Monedas válidas para exportación según DGI
const VALID_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'CHF', 'AUD', 'MXN', 'BRL', 'COP',
  'CRC', 'GTQ', 'HNL', 'NIO', 'PEN', 'DOP', 'ARS', 'CLP', 'UYU', 'BOB',
  'PYG', 'VES', 'CNY', 'KRW', 'INR', 'ZZZ',
];

export function Step2InvoiceDetails({ wizardData, onUpdate, onNext, onPrevious }: Step2InvoiceDetailsProps) {
  const { selectedCompany, isLoading: companyLoading } = useCompanies({ navigateOnSelect: false });
  const { data: branchOfficesData } = useBranchOffices();
  const [dateError, setDateError] = useState('');
  const [dateWarning, setDateWarning] = useState('');
  const [dueDateError, setDueDateError] = useState('');
  const [exportErrors, setExportErrors] = useState<Record<string, string>>({});
  const [invoiceNumberError, setInvoiceNumberError] = useState('');

  // Extraer el array de sucursales del objeto de respuesta
  const branchOffices = (branchOfficesData as any)?.branchOffices || [];

  // Buscar la sucursal seleccionada
  const selectedBranch = branchOffices?.find(
    (branch: any) => branch.id === wizardData.branchOfficeId
  );

  const isGobierno = wizardData.receiverType === '03';
  const isConsumidorFinal = wizardData.receiverType === '02';

  // Tipos de documento: mostrar todos (el portal legacy no restringe por receptor)
  const allowedDocumentTypes = DOCUMENT_TYPES;

  // Filtrar naturalezas de operación según destino
  // Extranjero: mostrar las de comercio exterior (02, 03, 04, 05, 21) + genéricas
  // Nacional: mostrar las nacionales (01, 10-14, 20) + genéricas
  const allowedNatures = OPERATION_NATURES;

  // Portal legacy: auto-seleccionar destino según tipo de documento, naturaleza de operación y tipo de receptor
  // No bloquea — solo pone el default, el usuario puede cambiarlo libremente
  useEffect(() => {
    const tiposDocumentoExtranjero = ['03', '10']; // Exportación, Operación Extranjera
    const naturalezasExtranjero = ['02', '03', '04', '05']; // Export, Re-export, Foreign source sale, Foreign service

    const debeSerExtranjero =
      tiposDocumentoExtranjero.includes(wizardData.documentType) ||
      naturalezasExtranjero.includes(wizardData.operationNature);

    if (debeSerExtranjero && wizardData.operationDestination !== '2') {
      onUpdate({ operationDestination: '2' });
    } else if (!debeSerExtranjero && wizardData.operationDestination !== '1') {
      onUpdate({ operationDestination: '1' });
    }
  }, [wizardData.documentType, wizardData.operationNature]);

  const handleInputChange = (field: keyof InvoiceWizardData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onUpdate({ [field]: e.target.value });
  };

  const handleRadioChange = (field: keyof InvoiceWizardData, value: string) => {
    onUpdate({ [field]: value });
  };

  // DGI B10 (1519): >30 días atrás = Notificación (warning amarillo)
  // DGI B10a (1520): >2 días hábiles en futuro = Rechazo (error rojo)
  useEffect(() => {
    if (wizardData.invoiceDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const [year, month, day] = wizardData.invoiceDate.split('-').map(Number);
      const invoiceDate = new Date(year, month - 1, day);
      invoiceDate.setHours(0, 0, 0, 0);
      const diffMs = today.getTime() - invoiceDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      // Fecha en el pasado
      if (diffDays > 30) {
        setDateWarning('Fecha mayor a 30 días atrás. La DGI enviará una notificación (DGI B10)');
        setDateError('');
      } else {
        setDateWarning('');
      }

      // Fecha en el futuro: contar días hábiles
      if (diffDays < 0) {
        let businessDays = 0;
        const current = new Date(today);
        current.setDate(current.getDate() + 1);
        while (current <= invoiceDate) {
          const dow = current.getDay();
          if (dow !== 0 && dow !== 6) businessDays++;
          current.setDate(current.getDate() + 1);
        }
        if (businessDays > 2) {
          setDateError('La fecha de emisión no puede ser mayor a 2 días hábiles en el futuro (DGI B10a)');
        } else {
          setDateError('');
        }
      } else if (diffDays <= 30) {
        setDateError('');
      }
    } else {
      setDateError('');
      setDateWarning('');
    }
  }, [wizardData.invoiceDate]);

  // Validar fecha de vencimiento >= fecha de emisión
  useEffect(() => {
    if (wizardData.invoiceDate && wizardData.dueDate) {
      if (wizardData.dueDate < wizardData.invoiceDate) {
        setDueDateError('La fecha de vencimiento no puede ser anterior a la fecha de emisión');
      } else {
        setDueDateError('');
      }
    } else {
      setDueDateError('');
    }
  }, [wizardData.invoiceDate, wizardData.dueDate]);

  // Sección de exportación cuando destino es Extranjero (iDest=2)
  const isExportDocument = wizardData.operationDestination === '2';
  useEffect(() => {
    if (!isExportDocument) {
      if (
        wizardData.exportIncoterms ||
        wizardData.exportCurrency ||
        wizardData.exportCurrencyDescription ||
        wizardData.exportExchangeRate ||
        wizardData.exportPortOfEmbarkation
      ) {
        onUpdate({
          exportIncoterms: undefined,
          exportCurrency: undefined,
          exportCurrencyDescription: undefined,
          exportExchangeRate: undefined,
          exportPortOfEmbarkation: undefined,
        });
      }
      setExportErrors({});
    }
  }, [isExportDocument]);


  // Validar número de factura manual (DGI B07)
  useEffect(() => {
    if (wizardData.invoiceNumberMode === 'manual') {
      if (wizardData.invoiceNumber && wizardData.invoiceNumber.replace(/0/g, '') === '') {
        setInvoiceNumberError('El número de factura no puede ser solo ceros (DGI B07)');
      } else {
        setInvoiceNumberError('');
      }
    } else {
      setInvoiceNumberError('');
    }
  }, [wizardData.invoiceNumber, wizardData.invoiceNumberMode]);

  // Validar campos de exportación (DGI B50a, B501-B505)
  // Aplica siempre que el destino de la operación sea Extranjero (DGI B14=2),
  // sin importar el tipo de documento (ficha tecnica DGI grupo B50 gFExp)
  useEffect(() => {
    if (!isExportDocument) {
      setExportErrors({});
      return;
    }
    const newErrors: Record<string, string> = {};
    if (!wizardData.exportIncoterms) {
      newErrors.incoterms = 'Incoterms es obligatorio para operaciones con destino Extranjero (DGI B501)';
    }
    // DGI B504: Tipo de cambio obligatorio si existe B502 (moneda seleccionada)
    if (wizardData.exportCurrency) {
      if (!wizardData.exportExchangeRate || wizardData.exportExchangeRate <= 0) {
        newErrors.exchangeRate = 'Tipo de cambio obligatorio cuando se selecciona moneda (DGI B504)';
      }
    }
    // DGI B503: Descripción obligatoria si moneda = ZZZ
    if (wizardData.exportCurrency === 'ZZZ' && !wizardData.exportCurrencyDescription?.trim()) {
      newErrors.currencyDescription = 'Descripción de moneda obligatoria cuando es ZZZ (DGI B503)';
    }
    setExportErrors(newErrors);
  }, [isExportDocument, wizardData.exportIncoterms, wizardData.exportCurrency, wizardData.exportExchangeRate, wizardData.exportCurrencyDescription]);

  // Validation logic
  const isReferenceRequired = wizardData.documentType === '04' || wizardData.documentType === '05';

  // Check if reference fields are filled when required
  const isReferenceValid = !isReferenceRequired || (
    wizardData.gDFRef?.dFechaDFRef && (
      wizardData.gDFRef?.dCufeRef ||
      wizardData.gDFRef?.dNroFacPap ||
      wizardData.gDFRef?.dNroFacIE
    )
  );

  const hasExportErrors = isExportDocument && Object.keys(exportErrors).length > 0;
  const hasManualNumberError = wizardData.invoiceNumberMode === 'manual' && !wizardData.invoiceNumber?.trim();

  const isFormValid = wizardData.invoiceDate && wizardData.dueDate && wizardData.branchOfficeId
    && isReferenceValid && !dateError && !dueDateError
    && !hasExportErrors && !hasManualNumberError && !invoiceNumberError;

  return (
    <div className="space-y-6" data-testid="invoice-wizard-step2-container">
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">Detalles de la Factura</h2>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Configura los detalles y parámetros de la factura electrónica
        </p>
      </div>

      {/* Banners informativos según contexto del receptor (enlace con Step 1) */}
      {isGobierno && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-400 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-purple-800 dark:text-purple-200">Receptor Gobierno</h3>
              <ul className="mt-1 text-sm text-purple-700 dark:text-purple-300 list-disc pl-4 space-y-0.5">
                <li>El destino queda fijado a <strong>Panamá</strong></li>
                <li>No puede emitir Facturas de Exportación ni de Operación Extranjera</li>
                <li>Recuerde que los ítems deben incluir el <strong>código CPBS completo</strong> (paso 3)</li>
                <li>Debe configurar la <strong>retención</strong> aplicable (paso 4)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {isConsumidorFinal && (
        <div className="bg-teal-50 dark:bg-teal-900/20 border-l-4 border-teal-400 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="h-5 w-5 text-teal-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-teal-800 dark:text-teal-200">Consumidor Final</h3>
              <ul className="mt-1 text-sm text-teal-700 dark:text-teal-300 list-disc pl-4 space-y-0.5">
                <li>El destino queda fijado a <strong>Panamá</strong></li>
                <li>Si el total supera B/10,000.00, la DGI generará una notificación (DGI D09c)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {(wizardData.documentType === '04' || wizardData.documentType === '05') && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Nota de Crédito/Débito referenciada</h3>
              <ul className="mt-1 text-sm text-amber-700 dark:text-amber-300 list-disc pl-4 space-y-0.5">
                <li>Debe completar la referencia al documento original (CUFE o número de factura)</li>
                <li>La fecha del documento referenciado no puede ser mayor a 180 días</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Emisor y Receptor Info Card */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Emisor */}
            <div>
              {selectedCompany ? (
                <div className="border-l-4 border-primary-500 pl-4">
                  <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
                    Emisor
                  </h3>
                  <p className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {selectedCompany.name}
                  </p>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Código: {selectedCompany.code}
                  </p>
                  {selectedBranch && (
                    <div className="mt-2 pt-2 border-t border-secondary-200 dark:border-secondary-700">
                      <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400">
                        Sucursal
                      </p>
                      <p className="text-sm font-semibold text-secondary-900 dark:text-white">
                        {selectedBranch.name}
                      </p>
                      <p className="text-xs text-secondary-600 dark:text-secondary-400">
                        Código Fiscal: {selectedBranch.fiscalCode}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-1">
                    ⚠️ Emisor
                  </h3>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    {companyLoading ? 'Cargando compañía seleccionada...' : 'No hay compañía seleccionada.'}
                  </p>
                </div>
              )}
            </div>

            {/* Receptor */}
            <div>
              {wizardData.customerName ? (
                <div className="border-l-4 border-success-500 pl-4">
                  <h3 className="text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
                    Receptor
                  </h3>
                  <p className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {wizardData.customerName}
                  </p>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    RUC: {wizardData.customerRuc}{wizardData.customerDv ? `-${wizardData.customerDv}` : ''}
                  </p>
                  {wizardData.customerEmail && (
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      Email: {wizardData.customerEmail}
                    </p>
                  )}
                </div>
              ) : (
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-1">
                    ⚠️ Receptor
                  </h3>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    No hay cliente seleccionado.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Fechas y Detalles */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
            Detalles de la Factura
          </h3>

          {/* Layout de 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* COLUMNA IZQUIERDA */}
            <div className="space-y-5">
              {/* Grupo: Identificación del Documento */}
              <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                  Identificación del Documento
                </h4>

                {/* Número de Factura */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Número de Factura
                  </label>
                  <div className="flex gap-4 mb-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="invoiceNumberMode"
                        value="auto"
                        checked={wizardData.invoiceNumberMode === 'auto'}
                        onChange={() => handleRadioChange('invoiceNumberMode', 'auto')}
                        className="mr-2"
                        data-testid="invoice-wizard-step2-invoice-number-mode-auto-radio"
                      />
                      <span className="text-sm text-secondary-700 dark:text-secondary-300">Automático</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="invoiceNumberMode"
                        value="manual"
                        checked={wizardData.invoiceNumberMode === 'manual'}
                        onChange={() => handleRadioChange('invoiceNumberMode', 'manual')}
                        className="mr-2"
                        data-testid="invoice-wizard-step2-invoice-number-mode-manual-radio"
                      />
                      <span className="text-sm text-secondary-700 dark:text-secondary-300">Manual</span>
                    </label>
                  </div>
                  {wizardData.invoiceNumberMode === 'manual' && (
                    <Input
                      label="Número de Factura"
                      value={wizardData.invoiceNumber}
                      onChange={handleInputChange('invoiceNumber')}
                      placeholder="Ingrese el número de factura"
                      maxLength={10}
                      data-testid="invoice-wizard-step2-invoice-number-input"
                    />
                  )}
                </div>

                {/* Tipo de Documento */}
                <div>
                  <Select
                    label="Tipo de Documento"
                    value={wizardData.documentType}
                    onChange={handleInputChange('documentType')}
                    data-testid="invoice-wizard-step2-document-type-select"
                  >
                    {allowedDocumentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.description} — {type.hint}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Naturaleza de la Operación */}
                <div>
                <Select
                  label="Naturaleza de la Operación"
                  value={wizardData.operationNature}
                  onChange={handleInputChange('operationNature')}
                  data-testid="invoice-wizard-step2-operation-nature-select"
                >
                  {allowedNatures.map((nature) => (
                    <option key={nature.id} value={nature.id}>
                      {nature.id} - {nature.description} — {nature.hint}
                    </option>
                  ))}
                </Select>
                </div>

                {/* Tipo de Transacción de Venta — DGI B19: Ocu 0-1, solo informar cuando es venta */}
                {wizardData.operationType === '1' && !['20', '21', '11'].includes(wizardData.operationNature) && (
                  <Select
                    label="Tipo de Transacción"
                    value={wizardData.salesTransactionType}
                    onChange={handleInputChange('salesTransactionType')}
                    data-testid="invoice-wizard-step2-sales-transaction-type-select"
                  >
                    {SALES_TRANSACTION_TYPES.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.description} — {type.hint}
                      </option>
                    ))}
                  </Select>
                )}
              </div>

              {/* Sección de Referencias (solo para Notas de Crédito/Débito tipos 04 y 05) */}
              {(wizardData.documentType === '04' || wizardData.documentType === '05') && (
                <div className="col-span-full">
                  <ReferenceSection wizardData={wizardData} onUpdate={onUpdate} />
                </div>
              )}

              {/* Grupo: Tipo de Operación */}
              <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                  Tipo de Operación
                </h4>

                {/* Tipo de Operación */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Tipo de Operación
                  </label>
                  <div className="flex gap-4">
                    {OPERATION_TYPES.map((type) => (
                      <label key={type.id} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="operationType"
                          value={type.id}
                          checked={wizardData.operationType === type.id}
                          onChange={() => handleRadioChange('operationType', type.id)}
                          className="mr-2"
                          data-testid={`invoice-wizard-step2-operation-type-radio-${type.id}`}
                        />
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">{type.description}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Destino de la Operación */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Destino de la Operación
                  </label>
                  <div className="flex gap-4">
                    {OPERATION_DESTINATIONS.map((destination) => (
                      <label key={destination.id} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="operationDestination"
                          value={destination.id}
                          checked={wizardData.operationDestination === destination.id}
                          onChange={() => handleRadioChange('operationDestination', destination.id)}
                          className="mr-2"
                          data-testid={`invoice-wizard-step2-operation-destination-radio-${destination.id}`}
                        />
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">{destination.description}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    Seleccione Panamá o Extranjero según el tipo de operación
                  </p>
                </div>

              </div>

              {/* Grupo: Fechas */}
              <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4">
                <h4 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                  Fechas
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Input
                      label="Fecha de Emisión"
                      type="date"
                      value={wizardData.invoiceDate}
                      onChange={handleInputChange('invoiceDate')}
                      required
                      className={dateError ? 'border-danger-500' : ''}
                      data-testid="invoice-wizard-step2-invoice-date-input"
                    />
                    {dateError && (
                      <p className="text-xs text-danger-600 dark:text-danger-400 mt-1" data-testid="invoice-wizard-step2-invoice-date-error">{dateError}</p>
                    )}
                    {dateWarning && !dateError && (
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1" data-testid="invoice-wizard-step2-invoice-date-warning">{dateWarning}</p>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Fecha de Vencimiento"
                      type="date"
                      value={wizardData.dueDate}
                      onChange={handleInputChange('dueDate')}
                      required
                      className={dueDateError ? 'border-danger-500' : ''}
                      data-testid="invoice-wizard-step2-due-date-input"
                    />
                    {dueDateError && (
                      <p className="text-xs text-danger-600 dark:text-danger-400 mt-1" data-testid="invoice-wizard-step2-due-date-input-error">{dueDateError}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA */}
            <div className="space-y-5">
              {/* Grupo: Configuración de Envío */}
              <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 space-y-4">
                <h4 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                  Configuración de Envío
                </h4>

                {/* Envío del Contenedor */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Envío del Contenedor
                  </label>
                  <div className="space-y-1">
                    {CONTAINER_SHIPPING.map((shipping) => (
                      <label key={shipping.id} className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="containerShipping"
                          value={shipping.id}
                          checked={wizardData.containerShipping === shipping.id}
                          onChange={() => handleRadioChange('containerShipping', shipping.id)}
                          className="mr-2 mt-1"
                          data-testid={`invoice-wizard-step2-container-shipping-radio-${shipping.id}`}
                        />
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">{shipping.description}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Generación de Documento en Formato */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Generación de Documento
                  </label>
                  <div className="space-y-1">
                    {DOCUMENT_FORMAT_GENERATIONS.map((format) => (
                      <label key={format.id} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="documentFormatGeneration"
                          value={format.id}
                          checked={wizardData.documentFormatGeneration === format.id}
                          onChange={() => handleRadioChange('documentFormatGeneration', format.id)}
                          className="mr-2"
                          data-testid={`invoice-wizard-step2-document-format-generation-radio-${format.id}`}
                        />
                        <span className="text-sm text-secondary-700 dark:text-secondary-300">{format.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sección de exportación - Cuando destino es Extranjero (iDest=2) */}
              {isExportDocument && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-4 border border-amber-200 dark:border-amber-800">
                  <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                    Datos de exportación
                  </h4>

                  <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
                    <p className="font-medium">Para exportación de bienes o servicios al extranjero:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      <li><strong>Incoterms:</strong> seleccione las condiciones de entrega pactadas con el comprador (ej: FOB, CIF)</li>
                      <li><strong>Moneda:</strong> la moneda en que se factura al comprador extranjero</li>
                      <li><strong>Tipo de cambio:</strong> tasa de conversión a USD (solo si la moneda no es USD)</li>
                      <li><strong>Puerto de embarque:</strong> punto de salida de la mercancía (opcional para servicios)</li>
                    </ul>
                  </div>

                  {/* Incoterms */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Condición de Entrega (Incoterms) <span className="text-danger-600">*</span>
                    </label>
                    <select
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white ${
                        exportErrors.incoterms ? 'border-danger-500' : 'border-secondary-300 dark:border-secondary-600'
                      }`}
                      value={wizardData.exportIncoterms || ''}
                      onChange={(e) => onUpdate({ exportIncoterms: e.target.value })}
                      data-testid="invoice-wizard-step2-incoterms-select"
                    >
                      <option value="">Seleccione Incoterms</option>
                      {VALID_INCOTERMS.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.id} - {item.description} — {item.hint}
                        </option>
                      ))}
                    </select>
                    {exportErrors.incoterms ? (
                      <p className="text-xs text-danger-600 mt-1" data-testid="invoice-wizard-step2-incoterms-error">{exportErrors.incoterms}</p>
                    ) : (
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                        {wizardData.exportIncoterms
                          ? VALID_INCOTERMS.find(i => i.id === wizardData.exportIncoterms)?.hint
                          : 'Define quién asume los costos y riesgos del transporte internacional'}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Moneda */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Moneda
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white ${
                          exportErrors.currency ? 'border-danger-500' : 'border-secondary-300 dark:border-secondary-600'
                        }`}
                        value={wizardData.exportCurrency || ''}
                        onChange={(e) => {
                          const val = e.target.value || undefined;
                          const updates: Partial<typeof wizardData> = { exportCurrency: val };
                          // Limpiar campos dependientes si quitan la moneda
                          if (!val) {
                            updates.exportExchangeRate = undefined;
                            updates.exportCurrencyDescription = undefined;
                          }
                          onUpdate(updates);
                        }}
                        data-testid="invoice-wizard-step2-export-currency-select"
                      >
                        <option value="">No especificar (se asume USD)</option>
                        {VALID_CURRENCIES.map((cur) => (
                          <option key={cur} value={cur}>{cur === 'ZZZ' ? 'ZZZ — Otra moneda no listada' : cur}</option>
                        ))}
                      </select>
                      {exportErrors.currency ? (
                        <p className="text-xs text-danger-600 mt-1" data-testid="invoice-wizard-step2-export-currency-error">{exportErrors.currency}</p>
                      ) : (
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                          Opcional — moneda en que se factura al comprador extranjero
                        </p>
                      )}
                    </div>

                    {/* Tipo de cambio - obligatorio si se seleccionó moneda */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Tipo de Cambio {wizardData.exportCurrency && <span className="text-danger-600">*</span>}
                      </label>
                      <Input
                        type="number"
                        step="0.0001"
                        min="0"
                        defaultValue={wizardData.exportExchangeRate || ''}
                        onBlur={(e) => onUpdate({ exportExchangeRate: parseFloat(e.target.value) || undefined })}
                        placeholder="1.0000"
                        className={exportErrors.exchangeRate ? 'border-danger-500' : ''}
                        disabled={!wizardData.exportCurrency}
                        data-testid="invoice-wizard-step2-export-exchange-rate-input"
                      />
                      {exportErrors.exchangeRate ? (
                        <p className="text-xs text-danger-600 mt-1" data-testid="invoice-wizard-step2-export-exchange-rate-error">{exportErrors.exchangeRate}</p>
                      ) : (
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                          {!wizardData.exportCurrency
                            ? 'Seleccione una moneda primero'
                            : 'Tasa de conversión a USD a la fecha de la operación'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* DGI B503: Descripción de moneda — obligatorio si B502=ZZZ */}
                  {wizardData.exportCurrency === 'ZZZ' && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Descripción de la Moneda <span className="text-danger-600">*</span>
                      </label>
                      <Input
                        defaultValue={wizardData.exportCurrencyDescription || ''}
                        onBlur={(e) => onUpdate({ exportCurrencyDescription: e.target.value })}
                        placeholder="Nombre de la moneda no listada"
                        maxLength={50}
                        helperText="Obligatorio cuando la moneda es ZZZ (DGI B503)"
                        data-testid="invoice-wizard-step2-export-currency-description-input"
                      />
                    </div>
                  )}

                  {/* Puerto de embarque */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Puerto de Embarque
                    </label>
                    <Input
                      defaultValue={wizardData.exportPortOfEmbarkation || ''}
                      onBlur={(e) => onUpdate({ exportPortOfEmbarkation: e.target.value || undefined })}
                      placeholder="Ej: Puerto de Balboa"
                      maxLength={50}
                      helperText="Punto de salida de la mercancía (opcional para servicios)"
                      data-testid="invoice-wizard-step2-export-port-of-embarkation-input"
                    />
                  </div>
                </div>
              )}

            </div>
          </div>
        </CardBody>
      </Card>

      {/* Errores de número de factura manual */}
      {(invoiceNumberError || hasManualNumberError) && (
        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-3" data-testid="invoice-wizard-step2-invoice-number-error">
          <p className="text-sm text-danger-700 dark:text-danger-400">
            {invoiceNumberError || 'Debe ingresar un número de factura en modo manual'}
          </p>
        </div>
      )}

      {/* Error de fecha de vencimiento */}
      {dueDateError && (
        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-3" data-testid="invoice-wizard-step2-due-date-error">
          <p className="text-sm text-danger-700 dark:text-danger-400">{dueDateError}</p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious} data-testid="invoice-wizard-step2-previous-button">
          Anterior
        </Button>
        <Button variant="primary" onClick={onNext} disabled={!isFormValid} data-testid="invoice-wizard-step2-next-button">
          Siguiente
        </Button>
      </div>
    </div>
  );
}
